import {jsx} from 'panel';

import {Segmentation} from 'common/types/reports/query';
import type {QueryEntrySection} from 'common/query-builder/query-entry-section';
import {SelectPropertyEvent} from 'common/query-builder/query-block/properties-menu/types';
import {QueryBlockEl} from 'common/query-builder/query-block';
import {MenuType} from 'common/query-builder/query-block/types';
import {serializeAttributes} from 'common/util';
import {qbStyleAttrs} from 'common/query-builder/query-builder/util';
import {getIconForPropertyType} from 'common/report/util/properties';
import {ControlBarEl} from 'common/widgets/control-bar';
import {ButtonTheme} from 'common/components/button/constants';
import {
  ControlBarAction,
  ControlBarActionItems,
  ControlBarActionValue,
} from 'common/query-builder/query-entry-section/constants';
import {getPropertyType, setPropertyType} from 'common/query-builder/query-entry-section/row-types/row-util';
import {
  propertySelectionToSegmentation,
  segmentationToPropertySelection,
} from 'common/query-builder/query-block/segment-menu/util';
import {TypecastSubitem} from 'common/query-builder/query-entry-section/components/typecast-subitem';
import {EntryPart} from 'common/query-builder/query-entry-section/types';
import {PropertyType} from 'common/report/constants';
import {COMPUTED_LIST_PROPERTIES_VALUES} from 'common/query-builder/query-block/properties-menu/constants';

/** Value selection for list of objects breakdowns */
export function SegmentListObjectPropertySelector({
  segment,
  section,
  entryRowIdx,
  handleSelect,
  numericOnly = false,
  hideOverflowControls = false,
}: {
  segment: Segmentation;
  entryRowIdx: number;
  section: QueryEntrySection;
  numericOnly?: boolean;
  hideOverflowControls?: boolean;
  handleSelect: (selection: Segmentation) => void;
}) {
  const segmentPropertyName = segment.listItemGroup?.propertyName;
  const canTypecast =
    typeof segmentPropertyName === `string` && !COMPUTED_LIST_PROPERTIES_VALUES.has(segmentPropertyName);

  const propertyType = segment.listItemGroup && getPropertyType(segment.listItemGroup);
  const overflowControls = !hideOverflowControls && segment.listItemGroup?.propertyName && (
    <ControlBarEl
      key='entry-actions'
      attrs={{
        elref: `lineActions`,
        theme: ButtonTheme.Tertiary,
        'overflow-icon': section.hasNewQBUI() ? `ellipsis` : `hor-ellipsis`,
        'overflow-controls': [
          {
            items: [canTypecast && ControlBarActionItems[ControlBarAction.Typecast](propertyType)].filter(Boolean),
          },
        ],
        'overflow-menu-width': 200,
        'overflow-menu-height': 330,
      }}
      on={{
        select: (ev: CustomEvent) => {
          const value = ev.detail.value as ControlBarActionValue;

          if (value.action === ControlBarAction.Typecast) {
            handleSelect(setPropertyType(segment.listItemGroup, value.toType, false));
          }
        },
      }}
    />
  );

  return (
    <>
      <div sel='.segment-list-object-property-selector'>
        <QueryBlockEl
          attrs={serializeAttributes({
            type: MenuType.Properties,
            'event-names': section.attr(`event-names`),
            'disable-value-selection': true,
            'data-group-id': section.attr(`data-group-id`),
            'project-has-cohorts': false,
            'project-id': section.attr(`project-id`),
            open: section.state.openEntry.index === entryRowIdx && section.state.openEntry.part === EntryPart.SubValue,
            selected: segmentationToPropertySelection(segment.listItemGroup),
            theme: qbStyleAttrs(section.hasNewQBUI()).selectorTheme,
            'selector-type': `button`,
            'selector-icon': getIconForPropertyType(propertyType) ?? ``,
            'user-id': section.attr(`user-id`),
            'workspace-id': section.attr(`workspace-id`),
            'type-filter': numericOnly ? [PropertyType.Number] : [],
          })}
          params={{
            listProperty: segment,
          }}
          on={{
            select: (ev: SelectPropertyEvent) => {
              handleSelect(propertySelectionToSegmentation(ev.detail.item) as Segmentation);
            },
            close: () => section.setActiveEntry({index: null, part: null}),
            open: () => section.setActiveEntry({index: entryRowIdx, part: EntryPart.SubValue}),
          }}
        />
        <div sel='.actions'>{overflowControls}</div>
      </div>
      {segment.listItemGroup && <TypecastSubitem value={segment.listItemGroup} onTypeChange={handleSelect} />}
    </>
  );
}
